import { useState, useEffect, useRef } from 'react';
import { Col, Pagination, Row } from 'react-bootstrap';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import classNames from 'classnames';
import usePagination from 'hooks/usePagination';

interface AdvanceTableFooterProps {
  className?: string;
  pagination?: boolean;
  navBtn?: boolean;
  showViewAllBtn?: boolean;
  currentPage: number; // External current page (1-based)
  totalPages?: number; // External total pages
  totalItems?: number;
  pageSize: number;
  onPageChange?: (page: number) => void; // External page change handler
  isLoadingCount?: boolean; // Loading state for count (for async count endpoints)
  countUnavailable?: boolean; // Count timed out or failed (e.g. 504)
  countErrorMessage?: string; // e.g. "Total count is unavailable; try narrowing filters."
}

const AdvanceTableFooter = ({
  className,
  pagination,
  navBtn,
  showViewAllBtn = true,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  isLoadingCount = false,
  countUnavailable = false,
  countErrorMessage,
}: AdvanceTableFooterProps) => {
  const {
    // setPageSize,
    getPaginationRowModel,
    getPrePaginationRowModel,
    getState,
  } = useAdvanceTableContext();

  // Derive effective current page and total pages
  const effectivePageIndex = currentPage !== undefined ? currentPage - 1 : 0;
  const effectiveTotalPages = totalPages !== undefined ? totalPages : 1;

  // Show loading whenever count is being fetched (avoids showing stale total after filter change)
  const showLoadingCount = isLoadingCount && !countUnavailable;

  // When count is unavailable (e.g. 504), show "—" for total
  const displayTotalItems = countUnavailable ? null : showLoadingCount ? null : (totalItems ?? 0);
  const displayTotalPages = countUnavailable ? null : showLoadingCount ? null : (totalPages ?? 1);

  const totalPageCount = typeof displayTotalPages === 'number' ? displayTotalPages : (totalPages ?? 1);
  const maxButtonCount = 5;
  const [isCurrentPage, setCurrentPage] = useState(() => currentPage ?? 1);
  const prevCurrentPageRef = useRef(currentPage ?? 1);

  // Keep local pagination window in sync with external current page (e.g. after list/count load)
  useEffect(() => {
    const prev = prevCurrentPageRef.current;
    if (currentPage != null && currentPage !== prev) {
      prevCurrentPageRef.current = currentPage;
      setCurrentPage(currentPage);
    }
  }, [currentPage]);

  const { visiblePaginationItems, hasNextEllipsis, hasPrevEllipsis } = usePagination({
    currentPageNo: isCurrentPage,
    totalPage: typeof displayTotalPages === 'number' ? displayTotalPages : 1,
    maxPaginationButtonCount: maxButtonCount,
  });

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      setCurrentPage(page);
      onPageChange(page); // Convert to 1-based index
    }
  };

  return (
    <Row className={classNames(className, 'align-items-center py-1')}>
      <Col className="d-flex fs-9">
        <p className="mb-0 d-none d-sm-block me-3 fw-semi-bold text-900">
          {pageSize * effectivePageIndex + 1} to {totalItems ? Math.min(pageSize * (effectivePageIndex + 1), totalItems) : pageSize * (effectivePageIndex + 1)}{' '}
          <span className="text-600">items of</span>{' '}
          {showLoadingCount ? (
            <span className="text-600 fst-italic">loading...</span>
          ) : countUnavailable ? (
            <>
              <span className="text-600">—</span>
              {countErrorMessage && (
                <span className="text-600 fst-italic ms-1" title={countErrorMessage}>
                  ({countErrorMessage})
                </span>
              )}
            </>
          ) : (
            displayTotalItems
          )}
        </p>
      </Col>
      {pagination && (
        <Col xs="auto">
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1 || isLoadingCount || countUnavailable} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

            {hasPrevEllipsis && <Pagination.Ellipsis onClick={() => setCurrentPage((prev) => Math.max(1, prev - maxButtonCount))} />}

            {visiblePaginationItems.map((page) => (
              <Pagination.Item key={page} active={page === currentPage} onClick={() => handlePageChange(page)}>
                {page}
              </Pagination.Item>
            ))}

            {hasNextEllipsis && <Pagination.Ellipsis onClick={() => setCurrentPage((prev) => Math.min(totalPageCount, prev + maxButtonCount))} />}

            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPageCount} />
            <Pagination.Last onClick={() => handlePageChange(totalPageCount)} disabled={currentPage === totalPageCount || isLoadingCount || countUnavailable} />
          </Pagination>
        </Col>
      )}
    </Row>
  );
};

export default AdvanceTableFooter;
